import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { spring } from '../motion/presets';
import { cx } from '../internal/cx';

export type ToastIntent = 'success' | 'error' | 'info' | 'warning';

export type ToastInput = {
  intent?: ToastIntent;
  title: string;
  description?: string;
  /** ms avant auto-dismiss (défaut 4000 ; 0 = persistant). */
  duration?: number;
};

export type ToastItem = Required<Pick<ToastInput, 'title'>> &
  Omit<ToastInput, 'title'> & { id: number; intent: ToastIntent };

type ToastContextValue = {
  push: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

/** `const { push } = useToast()` — nécessite `<ToastProvider>` en amont. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast() doit être utilisé sous <ToastProvider>');
  return ctx;
}

const INTENT_STYLES: Record<ToastIntent, { bar: string; label: string }> = {
  success: { bar: 'bg-bamboo-500', label: 'Succès' },
  error: { bar: 'bg-danger-500', label: 'Erreur' },
  info: { bar: 'bg-sky-500', label: 'Info' },
  warning: { bar: 'bg-sun-500', label: 'Attention' },
};

let nextId = 1;

// forwardRef requis : `AnimatePresence mode="popLayout"` mesure ses enfants via ref.
const ToastCard = forwardRef<HTMLLIElement, { toast: ToastItem; onDismiss: (id: number) => void }>(
  function ToastCard({ toast, onDismiss }, ref) {
  const remaining = useRef(toast.duration ?? 4000);
  const startedAt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // Auto-dismiss avec pause au hover/focus (spec 05 §4).
  const pause = useCallback(() => {
    if (!timer.current) return;
    remaining.current -= Date.now() - startedAt.current;
    clear();
  }, [clear]);

  const resume = useCallback(() => {
    if (remaining.current <= 0 || timer.current) return;
    startedAt.current = Date.now();
    timer.current = setTimeout(() => onDismiss(toast.id), remaining.current);
  }, [onDismiss, toast.id]);

  useEffect(() => {
    if ((toast.duration ?? 4000) > 0) resume();
    return clear;
  }, [toast.duration, resume, clear]);

  const intent = INTENT_STYLES[toast.intent];
  return (
    <motion.li
      ref={ref}
      layout
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.96 }}
      transition={spring.soft}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      className={cx(
        'pointer-events-auto relative flex w-80 max-w-[calc(100vw-2rem)] items-start gap-3 overflow-hidden',
        'rounded-lg border border-ink-600/60 bg-ink-700 p-4 pl-5 text-ink-100 shadow-pop',
      )}
      data-intent={toast.intent}
    >
      <span aria-hidden="true" className={cx('absolute inset-y-0 left-0 w-1.5', intent.bar)} />
      <div className="min-w-0 flex-1">
        <p className="font-display font-semibold leading-tight">
          <span className="sr-only">{intent.label} : </span>
          {toast.title}
        </p>
        {toast.description && (
          <p className="mt-1 text-sm text-ink-400">{toast.description}</p>
        )}
      </div>
      <button
        type="button"
        aria-label="Fermer la notification"
        onClick={() => onDismiss(toast.id)}
        className="-mr-1 -mt-1 rounded-full p-1.5 text-ink-400 hover:bg-ink-100/10 hover:text-ink-100"
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" fill="none">
          <path
            d="M3 3l10 10M13 3L3 13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </motion.li>
  );
  },
);

/**
 * Provider de toasts : pile animée en bas à droite, `aria-live="polite"`,
 * auto-dismiss avec pause au hover/focus.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // ⚠️ Mémoïsation manuelle NÉCESSAIRE : ToastProvider est en bailout compiler
  // (nextId++ sur un global) — identité du contexte non couverte (ADR 0010).
  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((input: ToastInput): number => {
    const id = nextId++;
    setToasts((prev) => [...prev, { intent: 'info', ...input, id }]);
    return id;
  }, []);

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <ul
            aria-live="polite"
            aria-label="Notifications"
            className="pointer-events-none fixed bottom-4 right-4 z-toast flex flex-col items-end gap-3"
          >
            <AnimatePresence mode="popLayout">
              {toasts.map((toast) => (
                <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
              ))}
            </AnimatePresence>
          </ul>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}
